import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  increment,
  limit,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const todayKey = () => toDateKey(new Date());
const norm = (value) => String(value || "").toLocaleLowerCase("tr-TR");
const plateNorm = (value) => String(value || "").toLocaleUpperCase("tr-TR").replace(/\s+/g, "");
const targetNorm = (value) => String(value || "").trim().toLocaleLowerCase("tr-TR");
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const HAZIR_BETON_SITE_COLLECTIONS = [
  "hazirbeton_zone_uretim",
  "hazirbeton_mikser_sevkiyat",
  "hazirbeton_pompa_hakedis",
];

export default function FormFill({ canBackfillDate = false, allowedPlates = [], canWriteNewTargets = false }) {
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [plates, setPlates] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [facilitiesLoaded, setFacilitiesLoaded] = useState(false);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey());
  const [targetValue, setTargetValue] = useState("");
  const [answers, setAnswers] = useState({});
  const [issueNotes, setIssueNotes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [previousSubmission, setPreviousSubmission] = useState(null);

  useEffect(() => {
    (async () => {
      const [formSnap, plateSnap] = await Promise.all([
        getDocs(query(collection(db, "kontrol_formlari"), where("active", "==", true))),
        getDocs(collection(db, "plates")),
      ]);
      const activeForms = formSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
      const plateList = plateSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((p) => p.plate && p.active === true)
        .sort((a, b) => String(a.plate).localeCompare(String(b.plate), "tr"));
      setForms(activeForms);
      setPlates(plateList);
      if (activeForms[0]) setSelectedFormId(activeForms[0].id);
    })();
  }, []);

  const fetchSites = async () => {
    if (sitesLoaded) return;
    setTargetsLoading(true);
    try {
      const siteMap = new Map();
      const addSite = (name, customerName = "") => {
        const cleanName = String(name || "").trim();
        if (!cleanName) return;
        const key = targetNorm(cleanName);
        if (!siteMap.has(key)) siteMap.set(key, { value: cleanName, label: cleanName, detail: customerName || "" });
      };

      for (const colName of HAZIR_BETON_SITE_COLLECTIONS) {
        const snap = await getDocs(collection(db, colName));
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          addSite(data.santiyeAdi, data.musteriAdi);
        });
      }

      setSiteOptions(Array.from(siteMap.values()).sort((a, b) => a.label.localeCompare(b.label, "tr")));
      setSitesLoaded(true);
    } finally {
      setTargetsLoading(false);
    }
  };

  const fetchFacilities = async () => {
    if (facilitiesLoaded) return;
    setTargetsLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "tesisler"), where("active", "==", true)));
      const seen = new Map();
      snap.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const name = String(data.tesisAdi || data.tesisAd || data.tesis || data.name || data.ad || data.title || docSnap.id || "").trim();
        if (!name) return;
        const key = targetNorm(name);
        if (!seen.has(key)) seen.set(key, { value: name, label: name, detail: "" });
      });
      setFacilityOptions(Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label, "tr")));
      setFacilitiesLoaded(true);
    } finally {
      setTargetsLoading(false);
    }
  };

  const selectedForm = forms.find((f) => f.id === selectedFormId) || null;
  const allowedPlateSet = useMemo(
    () => new Set((Array.isArray(allowedPlates) ? allowedPlates : []).map(plateNorm).filter(Boolean)),
    [allowedPlates]
  );

  const targetOptions = useMemo(() => {
    if (!selectedForm) return [];
    if (selectedForm.targetType === "site") return siteOptions;
    if (selectedForm.targetType === "facility") return facilityOptions;
    if (selectedForm.targetType !== "vehicle") return [];
    const vehicleType = norm(selectedForm.vehicleType);
    return plates.filter((p) => {
      const matchesType = !vehicleType || norm(p.aracCinsi || p.vehicleType || "").includes(vehicleType);
      const matchesAllowedPlate = allowedPlateSet.size === 0 || allowedPlateSet.has(plateNorm(p.plate));
      return matchesType && matchesAllowedPlate;
    }).map((p) => ({ ...p, value: p.plate, label: p.plate, detail: p.aracCinsi || "" }));
  }, [selectedForm, plates, allowedPlateSet, siteOptions, facilityOptions]);

  useEffect(() => {
    if (selectedForm?.targetType === "site") fetchSites();
    if (selectedForm?.targetType === "facility") fetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForm?.targetType]);

  useEffect(() => {
    setTargetValue("");
    setAnswers({});
    setIssueNotes({});
    setPreviousSubmission(null);
  }, [selectedFormId]);

  useEffect(() => {
    if (!selectedForm || !targetValue) {
      setPreviousSubmission(null);
      return;
    }

    (async () => {
      const snap = await getDocs(query(
        collection(db, "kontrol_kayitlari"),
        where("formId", "==", selectedForm.id),
        where("targetKey", "==", targetValue),
        where("dateKey", "==", selectedDateKey)
      ));
      setPreviousSubmission(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    })();
  }, [selectedForm, targetValue, selectedDateKey]);

  const setAnswer = (itemId, value) => {
    setAnswers((prev) => ({ ...prev, [itemId]: value }));
  };

  const setIssueNote = (itemId, value) => {
    setIssueNotes((prev) => ({ ...prev, [itemId]: value }));
  };

  const selectedPlate = selectedForm?.targetType === "vehicle"
    ? targetOptions.find((p) => plateNorm(p.value || p.plate) === plateNorm(targetValue))
    : null;

  const targetExists = useMemo(() => {
    if (!selectedForm || !targetValue.trim()) return false;
    if (selectedForm.targetType === "vehicle") {
      return targetOptions.some((p) => plateNorm(p.value || p.plate) === plateNorm(targetValue));
    }
    return targetOptions.some((p) => targetNorm(p.value || p.label) === targetNorm(targetValue));
  }, [selectedForm, targetValue, targetOptions]);

  const upsertControlIssue = async ({ item, submissionId, note }) => {
    const targetKey = targetValue.trim();
    const openIssueSnap = await getDocs(query(
      collection(db, "kontrol_eksiklikleri"),
      where("formId", "==", selectedForm.id),
      where("targetKey", "==", targetKey),
      where("itemId", "==", item.id),
      where("createdBy", "==", user?.email || ""),
      where("resolved", "==", false),
      limit(1)
    ));

    const seenEntry = {
      submissionId,
      dateKey: selectedDateKey,
      seenAt: Timestamp.now(),
      note: note || "",
      createdBy: user?.email || "",
    };

    if (!openIssueSnap.empty) {
      await updateDoc(doc(db, "kontrol_eksiklikleri", openIssueSnap.docs[0].id), {
        lastSeenAt: Timestamp.now(),
        lastSeenDateKey: selectedDateKey,
        lastSeenBy: user?.email || "",
        lastNote: note || "",
        repeatCount: increment(1),
        seenHistory: arrayUnion(seenEntry),
        contributors: arrayUnion(user?.email || ""),
        updatedAt: serverTimestamp(),
      });
      return "updated";
    }

    await addDoc(collection(db, "kontrol_eksiklikleri"), {
      formId: selectedForm.id,
      formName: selectedForm.name,
      sourceSubmissionId: submissionId,
      itemId: item.id,
      issue: item.label,
      targetType: selectedForm.targetType,
      targetKey,
      targetLabel: targetKey,
      plate: selectedForm.targetType === "vehicle" ? targetKey : "",
      siteName: selectedForm.targetType === "site" ? targetKey : "",
      facilityName: selectedForm.targetType === "facility" ? targetKey : "",
      tesis: selectedPlate?.tesis || (selectedForm.targetType === "facility" ? targetKey : ""),
      aracCinsi: selectedPlate?.aracCinsi || selectedForm.vehicleType || "",
      firstSeenAt: Timestamp.now(),
      firstSeenDateKey: selectedDateKey,
      lastSeenAt: Timestamp.now(),
      lastSeenDateKey: selectedDateKey,
      lastSeenBy: user?.email || "",
      lastNote: note || "",
      repeatCount: 1,
      seenHistory: [seenEntry],
      contributors: user?.email ? [user.email] : [],
      resolved: false,
      createdAt: serverTimestamp(),
      createdBy: user?.email || "",
    });
    return "created";
  };

  const submit = async () => {
    if (!selectedForm) return alert("Form seçin.");
    if (!targetValue.trim()) return alert("Hedef seçin veya yazın.");
    if (selectedForm.targetType === "vehicle") {
      const activeOption = targetOptions.some((p) => plateNorm(p.value || p.plate) === plateNorm(targetValue));
      if (!activeOption) return alert("Bu plakayı doldurma yetkiniz yok veya araç aktif değil.");
    } else if (!targetExists && !canWriteNewTargets) {
      return alert("Bu hedef mevcut listeden seçilmeli. Yeni şantiye/tesis yazma yetkiniz yok.");
    }
    const today = todayKey();
    const minBackfillDate = toDateKey(addDays(new Date(), -2));
    if (!canBackfillDate && selectedDateKey !== today) return alert("Bu kullanıcı sadece bugünün formunu doldurabilir.");
    if (canBackfillDate && (selectedDateKey < minBackfillDate || selectedDateKey > today)) {
      return alert("Form tarihi yalnızca bugün veya son 2 gün içinden seçilebilir.");
    }

    const missing = (selectedForm.items || []).filter((item) => {
      if (item.type === "text") return false;
      return !answers[item.id];
    });
    if (missing.length) return alert("Tüm uygun/uygun değil maddelerini işaretleyin.");

    setSubmitting(true);
    try {
      const payload = {
        formId: selectedForm.id,
        formName: selectedForm.name,
        formVersion: selectedForm.updatedAt || null,
        targetType: selectedForm.targetType,
        targetKey: targetValue.trim(),
        plate: selectedForm.targetType === "vehicle" ? targetValue.trim() : "",
        targetLabel: targetValue.trim(),
        dateKey: selectedDateKey,
        answers,
        issueNotes,
        createdAt: serverTimestamp(),
        createdBy: user?.email || "",
      };
      const submissionRef = await addDoc(collection(db, "kontrol_kayitlari"), payload);

      const issueItems = (selectedForm.items || []).filter((item) => {
        if (item.type === "ok_not_ok") {
          return item.createsIssue !== false && answers[item.id] === "not_ok";
        }
        if (item.type === "text") {
          return item.createsIssue !== false && String(answers[item.id] || "").trim();
        }
        return false;
      });

      let createdCount = 0;
      let updatedCount = 0;
      for (const item of issueItems) {
        const result = await upsertControlIssue({
          item,
          submissionId: submissionRef.id,
          note: String(item.type === "text" ? answers[item.id] : issueNotes[item.id] || "").trim(),
        });
        if (result === "created") createdCount += 1;
        if (result === "updated") updatedCount += 1;
      }

      const messageParts = ["Form kaydedildi."];
      if (createdCount) messageParts.push(`${createdCount} yeni eksiklik açıldı.`);
      if (updatedCount) messageParts.push(`${updatedCount} açık eksiklik tekrar görüldü olarak güncellendi.`);
      alert(messageParts.join(" "));
      setAnswers({});
      setIssueNotes({});
      setPreviousSubmission({ id: submissionRef.id, ...payload });
    } finally {
      setSubmitting(false);
    }
  };

  const today = todayKey();
  const minBackfillDate = toDateKey(addDays(new Date(), -2));

  return (
    <div style={pageStyle}>
      <section style={panelStyle}>
        <h2 style={titleStyle}>Form Doldur</h2>
        <div style={gridStyle}>
          <label>Form
            <select style={inputStyle} value={selectedFormId} onChange={(e) => setSelectedFormId(e.target.value)}>
              {forms.map((form) => <option key={form.id} value={form.id}>{form.name}</option>)}
            </select>
          </label>
          {canBackfillDate && (
            <label>Form Tarihi
              <input
                type="date"
                style={inputStyle}
                value={selectedDateKey}
                min={minBackfillDate}
                max={today}
                onChange={(e) => setSelectedDateKey(e.target.value || today)}
              />
            </label>
          )}
          <label>{selectedForm?.targetType === "vehicle" ? "Plaka" : selectedForm?.targetType === "site" ? "Şantiye" : "Tesis"}
            <input
              style={inputStyle}
              list="kontrol-target-options"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              placeholder={selectedForm?.targetType === "vehicle" ? "Plaka seçin" : canWriteNewTargets ? "Listeden seçin veya yeni ad yazın" : "Listeden seçin"}
            />
            <datalist id="kontrol-target-options">
              {targetOptions.map((p) => <option key={p.id || p.value} value={p.value}>{p.detail || ""}</option>)}
            </datalist>
            {targetsLoading && <div style={mutedStyle}>Liste yükleniyor...</div>}
            {selectedForm?.targetType !== "vehicle" && !canWriteNewTargets && targetValue.trim() && !targetExists && (
              <div style={errorTextStyle}>Yeni hedef yazma yetkiniz yok; listeden seçim yapın.</div>
            )}
          </label>
        </div>
        {selectedForm && (
          <div style={infoStyle}>
            {selectedForm.vehicleType || "Genel"} / {selectedForm.period === "daily" ? "Günlük" : "Kullanım öncesi"}
            {!canBackfillDate && <span> / Tarih: {new Date(`${today}T12:00:00`).toLocaleDateString("tr-TR")}</span>}
            {previousSubmission && <b style={{ color: "#166534" }}> Bu tarih için doldurulmuş.</b>}
          </div>
        )}
      </section>

      {selectedForm && targetValue && (
        <section style={panelStyle}>
          <h3 style={subTitleStyle}>{selectedForm.name}</h3>
          {(selectedForm.items || []).map((item) => (
            <div key={item.id} style={itemStyle}>
              <div>
                <b>{item.label}</b>
                {item.createsIssue !== false && item.type === "ok_not_ok" && (
                  <div style={mutedStyle}>Uygun değil seçilirse kontrol eksikliği olarak takip edilir.</div>
                )}
              </div>
              {item.type === "text" ? (
                <textarea
                  style={{ ...inputStyle, minHeight: 74 }}
                  value={answers[item.id] || ""}
                  onChange={(e) => setAnswer(item.id, e.target.value)}
                  placeholder="Açıklama"
                />
              ) : (
                <div style={{ display: "grid", gap: 8, minWidth: 220 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" onClick={() => setAnswer(item.id, "ok")} style={answers[item.id] === "ok" ? okActiveBtn : okBtn}>Uygun</button>
                    <button type="button" onClick={() => setAnswer(item.id, "not_ok")} style={answers[item.id] === "not_ok" ? badActiveBtn : badBtn}>Uygun Değil</button>
                  </div>
                  {answers[item.id] === "not_ok" && item.createsIssue !== false && (
                    <textarea
                      style={{ ...inputStyle, minHeight: 62, marginTop: 0 }}
                      value={issueNotes[item.id] || ""}
                      onChange={(e) => setIssueNote(item.id, e.target.value)}
                      placeholder="Eksiklik açıklaması"
                    />
                  )}
                </div>
              )}
            </div>
          ))}
          <button type="button" onClick={submit} disabled={submitting} style={primaryBtn}>
            {submitting ? "Gönderiliyor..." : "Gönder"}
          </button>
        </section>
      )}
    </div>
  );
}

const pageStyle = { maxWidth: 900, margin: "0 auto", display: "grid", gap: 12 };
const panelStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 };
const titleStyle = { margin: "0 0 12px", fontSize: 20 };
const subTitleStyle = { margin: "0 0 12px", fontSize: 17 };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 };
const inputStyle = { width: "100%", boxSizing: "border-box", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, marginTop: 4 };
const itemStyle = { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "start", borderBottom: "1px solid #e2e8f0", padding: "12px 0" };
const mutedStyle = { color: "#64748b", fontSize: 12, marginTop: 2 };
const errorTextStyle = { color: "#b91c1c", fontSize: 12, marginTop: 4 };
const infoStyle = { marginTop: 10, color: "#475569", fontSize: 13 };
const primaryBtn = { marginTop: 14, background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "10px 14px", cursor: "pointer" };
const okBtn = { border: "1px solid #86efac", background: "#f0fdf4", color: "#166534", borderRadius: 6, padding: "8px 12px", cursor: "pointer" };
const okActiveBtn = { ...okBtn, background: "#16a34a", color: "#fff" };
const badBtn = { border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: 6, padding: "8px 12px", cursor: "pointer" };
const badActiveBtn = { ...badBtn, background: "#dc2626", color: "#fff" };
